import { type ICharacter, Screen, setIsOverlayLogin, useApp, useAppDispatch, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import { CharacterCardActions } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo } from "react";

import { CharacterCardDropdownActions } from "@/components/search/types";
import { useCharacterActions } from "@/components/search/v2/hooks";
import useDiscoverFavorite from "@/pages/Discover/hooks/useDiscoverFavorite";
import useDiscoverNsfwSelection from "@/pages/Discover/hooks/useDiscoverNsfwSelection";

export type DiscoverRowCardAction = CharacterCardActions.CHAT | CharacterCardDropdownActions;

type UseDiscoverRowCardActionsParameters = {
  characters: readonly ICharacter[];
  onSelectCharacter: (character: ICharacter) => void;
  onLessLikeThis: (character: ICharacter) => void;
};

const useDiscoverRowCardActions = ({ characters, onSelectCharacter, onLessLikeThis }: UseDiscoverRowCardActionsParameters) => {
  const { likedBots, onUserLikebot, isLoading: isSettingsLoading } = useUserSettings();
  const { userId, isAuthenticated } = useUserInfo();
  const { applicationName } = useApp();
  const dispatch = useAppDispatch();
  const isNsfw = useDiscoverNsfwSelection();
  // Guests get the login overlay instead; with no request handed back the optimistic heart settles at once.
  const openLoginOverlay = useCallback(() => {
    dispatch(setIsOverlayLogin(true));
  }, [dispatch]);
  // Shared with the profile overlay; returns the request so the row can flip the heart while it settles.
  const handleFavorite = useDiscoverFavorite({ onGuest: openLoginOverlay });
  const charHits = useMemo(() => [...characters], [characters]);
  const { characterInfo, shareModalVisible, blockModalVisible, setShareModalVisible, setBlockModalVisible, characterCardActionsHandler } =
    useCharacterActions({
      charHits,
      nsfwToggle: isNsfw,
      screenName: Screen.DISCOVER,
      likedBots,
      onUserLikebot,
    });

  const findCharacter = useCallback(
    (characterId: string) => characters.find((candidate) => (candidate.character_id || candidate.id) === characterId),
    [characters],
  );

  const handleAction = useCallback(
    (action: DiscoverRowCardAction, characterId: string) => {
      // Less like this is owned by the page so the row and the overlay dismiss from the same list.
      if (action === CharacterCardDropdownActions.LESS_LIKE_THIS) {
        const character = findCharacter(characterId);
        if (character) {
          onLessLikeThis(character);
        }
        return;
      }

      void characterCardActionsHandler(action, characterId, "", userId);

      if (action !== CharacterCardActions.CHAT) {
        return;
      }

      const character = findCharacter(characterId);
      if (character) {
        onSelectCharacter(character);
      }
    },
    [characterCardActionsHandler, findCharacter, onLessLikeThis, onSelectCharacter, userId],
  );

  const shareModal = useMemo(() => {
    if (!shareModalVisible || characterInfo === null) {
      return undefined;
    }

    return {
      visible: shareModalVisible,
      setVisible: setShareModalVisible,
      characterId: characterInfo.character_id || characterInfo.id || "",
      currentUrlOrigin: window.location.origin,
      applicationName: applicationName || "",
    };
  }, [applicationName, characterInfo, setShareModalVisible, shareModalVisible]);

  const blockModal = useMemo(() => {
    if (!blockModalVisible) {
      return undefined;
    }

    return {
      visible: blockModalVisible,
      setVisible: setBlockModalVisible,
      isLoading: isSettingsLoading,
    };
  }, [blockModalVisible, isSettingsLoading, setBlockModalVisible]);

  return {
    handleAction,
    handleFavorite,
    isAuthenticated: Boolean(isAuthenticated),
    characterInfo,
    shareModal,
    blockModal,
  };
};

export default useDiscoverRowCardActions;
